/**
 * Union Claim Form — /associations/:id/claim
 * Signed-in user uploads their registration cert + designation + contact
 * details. Submits to POST /api/union-claims.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, Upload, ArrowLeft, Mail, Loader2, FileCheck2,
  Sparkles,
} from "lucide-react";

export default function UnionClaim() {
  const { id }   = useParams();
  const nav      = useNavigate();
  const { user } = useAuth();
  const fileRef  = useRef(null);

  const [assoc, setAssoc]         = useState(null);
  const [meta, setMeta]           = useState(null);
  const [existing, setExisting]   = useState(null);
  const [loading, setL]           = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [file, setFile]           = useState(null);
  const [form, setForm]           = useState({
    claimant_name:    user?.name || "",
    designation:      "Secretary",
    claimant_email:   user?.email || "",
    claimant_phone:   user?.phone || "",
    proposed_website: "",
    proposed_email:   "",
    proposed_phone:   "",
    note:             "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [pRes, mRes, cRes] = await Promise.all([
          api.get(`/public/associations/${id}`),
          api.get(`/union-claims/meta`),
          api.get(`/union-claims/mine/for/${id}`).catch(() => ({ data: { claim: null } })),
        ]);
        setAssoc(pRes.data);
        setMeta(mRes.data);
        setExisting(cRes.data.claim);
      } finally {
        setL(false);
      }
    })();
  }, [id]);

  async function submit(e) {
    e.preventDefault();
    if (!file) {
      toast.error("Attach your registration certificate first"); return;
    }
    if (file.size > (meta?.max_cert_bytes || 0)) {
      toast.error(`File exceeds ${Math.round((meta.max_cert_bytes || 0) / (1024*1024))} MB`); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("association_id", id);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("cert", file);
      await api.post(`/union-claims`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("Claim submitted — our onboarding team will email you within 24h");
      nav(`/my-claims`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    } finally { setSubmitting(false); }
  }

  if (loading) {
    return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white/40 uppercase tracking-[0.3em] text-xs">Loading…</div>;
  }
  if (!assoc) {
    return <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center text-white/60">Association not found.</div>;
  }

  // Redirect: user is already an association_admin
  const alreadyAdmin = user?.account_type === "association_admin";

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="union-claim-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => nav(`/associations/${id}`)}
          className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
          data-testid="claim-back">
          <ArrowLeft size={12} /> {assoc.name} profile
        </button>

        <div className="mt-3 text-[10px] tracking-[0.32em] uppercase" style={{ color: assoc.color }}>
          Union Owner Onboarding
        </div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight" style={{ color: assoc.color }}
          data-testid="claim-title">
          Claim {assoc.name}
        </h1>
        <div className="mt-2 text-sm text-white/60 max-w-xl">
          Verify you're the secretariat — upload your union's registration certificate
          and we'll turn on your private analytics dashboard within 24h.
          Any questions, our onboarding desk is at{" "}
          <a href={`mailto:${meta?.onboarding_email}`} className="text-fuchsia-400 hover:text-fuchsia-300"
            data-testid="claim-onboarding-mail">
            {meta?.onboarding_email}
          </a>.
        </div>

        {/* Existing claim state */}
        {existing && ["pending", "info_requested"].includes(existing.status) && (
          <div className="mt-6 rounded-2xl border p-5"
            style={{ borderColor: "#F5B82E55", background: "#F5B82E10" }}
            data-testid="claim-existing-open">
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300">
              You already have an open claim
            </div>
            <div className="mt-2 text-sm">
              Status: <b>{existing.status.replace("_", " ")}</b>.
              {existing.admin_note && (
                <div className="mt-2 border-l-2 border-amber-400/40 pl-3 text-amber-100">
                  {existing.admin_note}
                </div>
              )}
            </div>
            <Link to="/my-claims"
              className="mt-3 inline-block text-[10px] uppercase tracking-[0.24em] text-amber-300 hover:text-amber-200"
              data-testid="claim-view-mine">
              View my claims →
            </Link>
          </div>
        )}
        {alreadyAdmin && (
          <div className="mt-6 rounded-2xl border border-fuchsia-500/40 bg-fuchsia-500/10 p-5"
            data-testid="claim-already-admin">
            <div className="text-sm">
              You're already registered as a union admin. To switch or add
              another union, write to <b>{meta?.onboarding_email}</b>.
            </div>
          </div>
        )}

        {/* Claim form */}
        {(!existing || !["pending", "info_requested"].includes(existing.status)) && !alreadyAdmin && (
          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="claim-form">

            {/* You */}
            <fieldset className="rounded-2xl border border-white/10 p-6">
              <legend className="px-2 text-[10px] uppercase tracking-[0.28em] text-white/50">
                Your details
              </legend>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full Name *">
                  <Input required value={form.claimant_name} data-testid="claim-name"
                    onChange={e => setForm({...form, claimant_name: e.target.value})}
                    className="bg-black/40 border-white/15" />
                </Field>
                <Field label="Designation *">
                  <Select value={form.designation}
                    onValueChange={v => setForm({...form, designation: v})}>
                    <SelectTrigger data-testid="claim-designation"
                      className="bg-black/40 border-white/15">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {meta?.designations.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Your Email *">
                  <Input required type="email" value={form.claimant_email}
                    data-testid="claim-email"
                    onChange={e => setForm({...form, claimant_email: e.target.value})}
                    className="bg-black/40 border-white/15" />
                </Field>
                <Field label="Your Phone">
                  <Input value={form.claimant_phone}
                    data-testid="claim-phone"
                    onChange={e => setForm({...form, claimant_phone: e.target.value})}
                    className="bg-black/40 border-white/15" />
                </Field>
              </div>
            </fieldset>

            {/* Certificate */}
            <fieldset className="rounded-2xl border border-white/10 p-6">
              <legend className="px-2 text-[10px] uppercase tracking-[0.28em] text-white/50">
                Registration certificate *
              </legend>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={e => setFile(e.target.files?.[0] || null)}
                data-testid="claim-cert-input" />
              <button type="button"
                onClick={() => fileRef.current?.click()}
                className="mt-2 w-full rounded-xl border-2 border-dashed border-white/15 hover:border-white/30 transition p-8 text-center flex flex-col items-center gap-2"
                data-testid="claim-cert-drop">
                {file ? (
                  <>
                    <FileCheck2 size={22} className="text-emerald-400" />
                    <div className="text-sm font-medium">{file.name}</div>
                    <div className="text-[11px] text-white/45">
                      {(file.size / 1024).toFixed(1)} KB · click to replace
                    </div>
                  </>
                ) : (
                  <>
                    <Upload size={22} className="text-white/40" />
                    <div className="text-sm">Choose your union's registration certificate</div>
                    <div className="text-[11px] text-white/45">
                      PDF · JPG · PNG · WebP — max {Math.round((meta?.max_cert_bytes || 0) / (1024*1024))} MB
                    </div>
                  </>
                )}
              </button>
            </fieldset>

            {/* Optional proposed contact */}
            <fieldset className="rounded-2xl border border-white/10 p-6">
              <legend className="px-2 text-[10px] uppercase tracking-[0.28em] text-white/50">
                Contact info to publish on {assoc.name} profile (optional)
              </legend>
              <div className="text-[11px] text-white/45 mt-1 mb-4">
                Leave blank if you'd rather set these later from your admin panel.
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Website">
                  <Input value={form.proposed_website}
                    data-testid="claim-prop-website"
                    onChange={e => setForm({...form, proposed_website: e.target.value})}
                    placeholder="https://…"
                    className="bg-black/40 border-white/15" />
                </Field>
                <Field label="Public email">
                  <Input value={form.proposed_email}
                    data-testid="claim-prop-email"
                    onChange={e => setForm({...form, proposed_email: e.target.value})}
                    placeholder="office@…"
                    className="bg-black/40 border-white/15" />
                </Field>
                <Field label="Public phone">
                  <Input value={form.proposed_phone}
                    data-testid="claim-prop-phone"
                    onChange={e => setForm({...form, proposed_phone: e.target.value})}
                    className="bg-black/40 border-white/15" />
                </Field>
              </div>
            </fieldset>

            {/* Note */}
            <Field label="Anything else our onboarding team should know?">
              <Textarea value={form.note}
                data-testid="claim-note"
                onChange={e => setForm({...form, note: e.target.value})}
                placeholder="Optional — special jurisdiction, sub-chapters we should map, etc."
                className="bg-black/40 border-white/15 min-h-[80px]" />
            </Field>

            <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 flex items-center gap-1.5">
                <ShieldCheck size={11} /> Documents stay encrypted at rest
              </div>
              <Button type="submit" disabled={submitting}
                className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-6 h-11"
                data-testid="claim-submit">
                {submitting
                  ? (<><Loader2 size={12} className="animate-spin mr-2" /> Submitting…</>)
                  : (<>Submit claim <Sparkles size={12} className="ml-2" /></>)}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
